import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Layers, Globe, Activity, ScrollText } from 'lucide-react';
import { SplitView, Section } from '@vnetwork/ui/layout';
import { StatusPill } from '@vnetwork/ui/data';
import { CompactStat, CompactStatGroup } from '../../components/CompactStat';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Badge,
  Button,
  Input,
  Explainer,
  LinkCard,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Spinner,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useClusterNodes,
  useClusterPools,
  useClusterIngressDetail,
  useUpdateCluster,
  machineReady,
  type K8sCluster,
} from '@/api/k8s';
import { DescList, DescItem, monoValue } from '../../components/DescList';
import { ProvisioningLogs } from './ProvisioningLogs';

export function Overview({ serviceId, clusterId, cluster }: { serviceId: string; clusterId: string; cluster: K8sCluster }) {
  const navigate = useNavigate();
  const m = cluster.metadata;
  const net = m?.network;
  const { data: nodeData } = useClusterNodes(serviceId, clusterId);
  const machines = nodeData?.machines ?? [];
  const { data: pools = [] } = useClusterPools(serviceId, clusterId);
  const { data: ingress, isLoading: ingressLoading } = useClusterIngressDetail(serviceId, clusterId);
  const [edit, setEdit] = useState(false);

  // Master readiness comes from the `kubernetes-controlplane-nodes` HAProxy backend the
  // server already isolates for this endpoint. That backend has one `server` line per
  // master node plus the aggregate BACKEND row, so each non-BACKEND row is one master:
  // total is the real provisioned master count and ready is the rows reporting UP.
  // `stats` is normalised to an array of `{values[]}` groups upstream, so flatten first.
  const masterServers = (ingress?.stats ?? [])
    .flatMap((g) => g.values ?? [])
    .filter((v) => v.server_name !== 'BACKEND');
  const totalMaster = masterServers.length;
  const masterReady = masterServers.filter((v) => v.status === 'UP').length;
  const masterDot = masterReady === 0 ? 'var(--vn-danger-solid)' : masterReady < totalMaster ? 'var(--vn-warning-solid)' : 'var(--vn-success-solid)';
  const masterHint = masterReady === 0 ? 'Not ready' : masterReady < totalMaster ? 'Warning' : 'Healthy';

  // Worker readiness comes off each provisioned machine's status (the k8s `nodes` array
  // is often empty while machines are still joining). Desired falls back to the pool spec
  // in metadata before any machine exists, so the panel never shows a bare "0/0".
  const desiredFromPools = (m?.pools ?? []).reduce((sum, p) => sum + (p.machines?.length ?? p.min ?? 0), 0);
  const readyNodes = machines.filter(machineReady).length;
  const workersTotal = machines.length || desiredFromPools;
  const poolCount = pools.length || (m?.pools?.length ?? 0);
  const lbIp = ingress?.ingress?.status?.loadBalancer?.ingress?.[0]?.ip;
  const addons = Object.entries(m?.addons ?? {}).filter(([, v]) => v?.enabled).map(([k]) => k);
  const fqdn = m?.endpoint?.fqdn;

  // A cluster reports active/running/ready only once every provisioning step finishes;
  // any other state (provisioning, pending, draft, …) means it's still coming up. The
  // detail query polls (useCluster.refetchInterval), so this banner refreshes live and
  // clears itself the moment the cluster turns ready.
  const provisionState = cluster.status || cluster.state || '';
  const isReady = /active|running|ready/i.test(provisionState);

  const base = `/services/kubernetes/${serviceId}/clusters/${clusterId}`;
  // Quick-action LinkCards render as <a> for a11y; intercept for SPA navigation.
  const go = (path: string) => (e: React.MouseEvent) => { e.preventDefault(); navigate(path); };

  const editAction = <Button size="sm" variant="secondary" startIcon={<Pencil size={15} />} onClick={() => setEdit(true)}>Edit</Button>;

  // Rail: how you reach the cluster + shortcuts to the other tabs.
  const rail = (
    <>
      <Card flat>
        <CardHeader><CardTitle style={{ fontSize: 'var(--vn-font-size-md)' }}>Connection</CardTitle></CardHeader>
        <CardBody>
          <DescList minColumn={999} style={{ gap: 'var(--vn-space-3)' }}>
            <DescItem label="API endpoint"><code style={{ ...monoValue, wordBreak: 'break-all' }}>{fqdn || '—'}</code></DescItem>
            <DescItem label="Ingress LB IP">{lbIp ? <code style={monoValue}>{lbIp}</code> : '—'}</DescItem>
          </DescList>
        </CardBody>
      </Card>

      <Section title="Quick actions">
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          <LinkCard icon={<Layers size={16} />} title="Node pools" description="Worker pools & sizing" href={`${base}/pools`} onClick={go(`${base}/pools`)} />
          <LinkCard icon={<Globe size={16} />} title="Ingress" description="Routes & load balancer" href={`${base}/ingress`} onClick={go(`${base}/ingress`)} />
          <LinkCard icon={<Activity size={16} />} title="Metrics" description="Resource utilisation" href={`${base}/metrics`} onClick={go(`${base}/metrics`)} />
          <LinkCard icon={<ScrollText size={16} />} title="Logs" description="Cluster event logs" href={`${base}/logs`} onClick={go(`${base}/logs`)} />
        </div>
      </Section>
    </>
  );

  return (
    <SplitView asideWidth="300px" aside={rail}>
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Provisioning progress — shown while the cluster is still coming up. Clears
          automatically once polling reports a ready state. */}
      {!isReady && (
        <Alert
          tone="info"
          icon={<Spinner size="sm" />}
          title="Cluster is provisioning"
        >
          The cluster is in the{' '}
          <strong style={{ textTransform: 'capitalize' }}>{provisionState || 'provisioning'}</strong> state and will
          become accessible once all provisioning steps finish. This page updates automatically.
          {/* Live deployment log streams inside the same info banner, tailing line by line. */}
          <ProvisioningLogs serviceId={serviceId} clusterId={clusterId} />
        </Alert>
      )}

      {/* Node-count KPIs — at-a-glance counts in a framed panel. */}
      <Card flat>
        <CardBody>
          <CompactStatGroup columns={4}>
            <CompactStat
              label="Masters ready"
              value={ingressLoading || totalMaster === 0 ? '—' : `${masterReady}/${totalMaster}`}
              dotColor={ingressLoading || totalMaster === 0 ? undefined : masterDot}
              hint={ingressLoading || totalMaster === 0 ? undefined : masterHint}
            />
            <CompactStat label="Worker nodes" value={String(workersTotal)} />
            <CompactStat label="Nodes ready" value={`${readyNodes}/${workersTotal}`} />
            <CompactStat label="Node pools" value={String(poolCount)} />
          </CompactStatGroup>
        </CardBody>
      </Card>

      {/* Identity — what the cluster is, separate from how you reach it (rail). */}
      <Card>
        <CardHeader actions={editAction}><CardTitle>Cluster information</CardTitle></CardHeader>
        <CardBody>
          <DescList>
            <DescItem label="Name">{cluster.name || m?.name || '—'}</DescItem>
            <DescItem label="Status">{(() => { const s = cluster.status || cluster.state || ''; return s ? <StatusPill tone={/active|running|ready/i.test(s) ? 'success' : 'muted'}>{s.charAt(0).toUpperCase() + s.slice(1)}</StatusPill> : '—'; })()}</DescItem>
            <DescItem label="Version">{cluster.version?.gitVersion ? `${cluster.version.gitVersion}${cluster.version.platform ? ` (${cluster.version.platform})` : ''}` : (m?.version || '—')}</DescItem>
            <DescItem label="Runtime">{m?.runtime || '—'}</DescItem>
            <DescItem label="Zone">{cluster.zone || '—'}</DescItem>
            <DescItem label="Cluster ID"><code style={monoValue}>{cluster.id}</code></DescItem>
            {cluster.description && <DescItem label="Description">{cluster.description}</DescItem>}
          </DescList>
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle>Networking</CardTitle></CardHeader>
        <CardBody>
          <DescList>
            <DescItem label="CNI">{net?.cni || '—'}</DescItem>
            <DescItem label="Pod CIDR"><code style={monoValue}>{net?.pod_cidr || '—'}</code></DescItem>
            <DescItem label="Service CIDR"><code style={monoValue}>{net?.service_cidr || '—'}</code></DescItem>
            <DescItem label="API endpoint"><code style={{ ...monoValue, wordBreak: 'break-all' }}>{fqdn || '—'}</code></DescItem>
            <DescItem label="Ingress LB IP">{lbIp ? <code style={monoValue}>{lbIp}</code> : '—'}</DescItem>
          </DescList>
        </CardBody>
      </Card>

      {addons.length > 0 && (
        <div>
          <Explainer title="Add-ons" description="Cluster add-ons currently enabled." defaultOpen>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
              {addons.map((a) => <Badge key={a} tone="success" appearance="subtle" size="sm">{a.replace(/_/g, ' ')}</Badge>)}
            </div>
          </Explainer>
        </div>
      )}

      {pools.length > 0 && (
        <Section title="Node pools" description="Worker pools and their sizing at a glance." style={{ marginBottom: 0 }}>
          <Card><CardBody style={{ padding: 0 }}>
            {pools.map((p, i) => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--vn-space-3) var(--vn-space-4)', borderTop: i ? '1px solid var(--vn-border-default)' : 'none', fontSize: 'var(--vn-font-size-sm)' }}>
                <span style={{ fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'] }}>{p.name}</span>
                {/* Backend zeroes minSize/maxSize for manual pools (autoscaler-hiding); prefer actualMinSize/actualMaxSize. */}
                <span style={{ color: 'var(--vn-fg-muted)' }}>{p.instanceType || p.metadata?.flavor} · {p.totalNodes ?? p.currentSize ?? 0} nodes · {p.actualMinSize ?? p.minSize ?? p.min}–{p.actualMaxSize ?? p.maxSize ?? p.max}{p.autoscaling ? ' (auto)' : ''}</span>
              </div>
            ))}
          </CardBody></Card>
        </Section>
      )}
      </div>

      {edit && <ClusterEditDialog serviceId={serviceId} clusterId={clusterId} cluster={cluster} onClose={() => setEdit(false)} />}
    </SplitView>
  );
}

function ClusterEditDialog({ serviceId, clusterId, cluster, onClose }: { serviceId: string; clusterId: string; cluster: K8sCluster; onClose: () => void }) {
  const { show } = useToast();
  const [name, setName] = useState(cluster.name ?? '');
  const [description, setDescription] = useState(cluster.description ?? '');
  const update = useUpdateCluster(serviceId, clusterId, {
    onSuccess: () => { show({ tone: 'success', title: 'Cluster updated' }); onClose(); },
    onError: () => show({ tone: 'danger', title: 'Update failed' }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit cluster</DialogTitle></DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <Field label="Name">{(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
            <Field label="Description">{(p) => <Input {...p} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" />}</Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!name.trim() || update.isPending} onClick={() => update.mutate({ name: name.trim(), description })}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

